import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Account, STORE_URL } from '@insite-store360/shared';
import { catchError, Observable } from 'rxjs';
import { ErrorHandlerService } from './error-handler.service';
@Injectable({
  providedIn: 'root',
})
export class AccountserviceService {
  private readonly ACCOUNT_API = `${STORE_URL}/account`;

  constructor(
    private http: HttpClient,
    private errorHandler: ErrorHandlerService
  ) { }

  /**
   * Api for getAll Account Infon
   */
  public accountGetAll(
    pageIndex: number,
    pageSize: number,
    searchFilter?: string,
    status: number = 2,
    countryIds?: number[]
  ): Observable<any> {
    const params: string[] = [
      `pageIndex=${pageIndex}`,
      `pageSize=${pageSize}`,
      `status=${status}`
    ];
    if (searchFilter) params.push(`searchFilter=${encodeURIComponent(searchFilter)}`);
    if (countryIds?.length) params.push(`countryIds=${countryIds.join(',')}`);
    const query = params.join('&');
    return this.http
      .get<any>(`${this.ACCOUNT_API}/getAll?${query}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Sends a POST request to the server to add a new store's information.
   * @param accountDetails - The store data to be added
   * @returns
   */
  public addAccoountDetails(accountDetails: {
    id: number;
    name: string;
    channelId: any;
    countryIds: any[];
  }): Observable<any> {
    console.log(accountDetails);

    return this.http
      .post<any>(`${this.ACCOUNT_API}/save`, accountDetails)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Toggel status.
   * @param id - The unique identifier of the status
   */

  updateToggleAccountStatus(id: number): Observable<any> {
    return this.http
      .put<any>(`${this.ACCOUNT_API}/changeStatus/${id}`, null)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Deletes a channel from the server.
   * @param id - The unique identifier of the channel to be deleted.
   * @returns An Observable that emits a confirmation message upon successful deletion.
   */
  public deleteAccount(id: number): Observable<string> {
    return this.http
      .delete<string>(`${this.ACCOUNT_API}/delete/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  // to get active channel status
  public getAllActiveAccount(searchFilter?: string, countryId?: number): Observable<any> {
    let queryParams = new HttpParams();
    if (searchFilter) {
      queryParams = queryParams.set('searchFilter', searchFilter);
    }
    if (countryId) {
      queryParams = queryParams.set('countryId', countryId);
    }
    return this.http
      .get<any>(`${this.ACCOUNT_API}/getAllActive`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * export accounts
   * @returns blob
   */
  public exportAccounts(): Observable<Blob> {
    const columns: string[] = ['Key Account', 'Channel', 'Country']; //export needed columns
    // const columns:string[] = []; //export all columns

    return this.http
      .post<Blob>(
        `${this.ACCOUNT_API}/downloadExcelReport`,
        { columns },
        {
          responseType: 'blob' as 'json',
        }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  // to get the dropdown for regions and country in text in excel
  public downloadExcelTemplate(countryId?: number) {
    let params = new HttpParams();
    if (countryId) {
      params = params.set('countryId', String(countryId));
    }
    return this.http
      .get(`${this.ACCOUNT_API}/downloadExcelTemplate`, {
        responseType: 'blob',
        params,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }
  /**
   * Updates Account information on the server.
   * @param id - The unique identifier of the Account to be updated.
   * @param data - The Account data to be updated.
   * @returns An Observable that emits the Account.
   */
  public updateAccount(AccountData: Account): Observable<any> {
    return this.http
      .put<any>(`${this.ACCOUNT_API}/update`, AccountData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public importAccountData(data: any): Observable<Account[]> {
    return this.http
      .post<Account[]>(`${this.ACCOUNT_API}/importAccountData`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAccountByChannelId(countryId: number, channelId: number, searchFilter?: string): Observable<any> {
    let queryParams = new HttpParams();
    if (searchFilter) {
      queryParams = queryParams.set('searchFilter', searchFilter);
    }
    if (countryId) {
      queryParams = queryParams.set('countryId', String(countryId));
    }
    if (channelId) {
      queryParams = queryParams.set('channelId', String(channelId));
    }
    return this.http.get<any>(`${this.ACCOUNT_API}/getAccountByChannelId`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAccountIdForPic(): Observable<any> {
    return this.http.get<any>(`${this.ACCOUNT_API}/getAccountidforPic`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAccountByCountry(countryId?: number): Observable<any> {
    let queryParams = new HttpParams();
    if (countryId) {
      queryParams = queryParams.set('countryId', countryId);
    }
    return this.http.post<any>(`${this.ACCOUNT_API}/getAccountsByCountryId`, {}, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Trade Calendar wizard — accounts with country/channel labels.
   * Does not use shared getAccountByCountryIdChannelId (sellout / price-mapping / etc.).
   */
  public getAccountsForTradeCalendar(
    countryIds: number[],
    channelIds: number[],
    searchFilter?: string,
  ): Observable<any> {
    let queryParams = new HttpParams();
    if (countryIds?.length) {
      queryParams = queryParams.set('countryId', countryIds.join(','));
    }
    if (channelIds?.length) {
      queryParams = queryParams.set('channelId', channelIds.join(','));
    }
    if (searchFilter) {
      queryParams = queryParams.set('searchFilter', searchFilter);
    }
    return this.http
      .get<any>(`${this.ACCOUNT_API}/getAccountsForTradeCalendar`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /** Store 360 Account tab — regionId is required; countryId and stateId are optional. */
  public getAccountsByRegion(
    regionIds: number[],
    countryIds?: number[],
    stateIds?: number[],
    searchFilter?: string,
  ): Observable<any> {
    let queryParams = new HttpParams();
    queryParams = queryParams.set('regionId', regionIds.join(','));
    if (countryIds?.length) {
      queryParams = queryParams.set('countryId', countryIds.join(','));
    }
    if (stateIds?.length) {
      queryParams = queryParams.set('stateId', stateIds.join(','));
    }
    if (searchFilter) {
      queryParams = queryParams.set('searchFilter', searchFilter);
    }
    return this.http
      .get<any>(`${this.ACCOUNT_API}/getAccountsByRegion`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public updateToggleInvoiceRequired(id: number): Observable<any> {
    return this.http
      .put<any>(`${this.ACCOUNT_API}/changeInvoiceStatus/${id}`, null)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public updateToggleEnterPrice(id: number): Observable<any> {
    return this.http
      .put<any>(`${this.ACCOUNT_API}/changeEnterPriceStatus/${id}`, null)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public updateToggleTest(id: number, deleteSurveyRecords?: boolean): Observable<any> {
    const body = deleteSurveyRecords === undefined ? {} : { deleteSurveyRecords };
    return this.http
      .put<any>(`${this.ACCOUNT_API}/changeTestStatus/${id}`, body)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /** Active Sold-To Party codes for one account -- feeds the Store form's mapping picker. */
  public getSoldToCodes(accountId: number): Observable<any> {
    return this.http
      .get<any>(`${this.ACCOUNT_API}/${accountId}/soldToCodes`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /** Every active Sold-To code across every account, with its account name -- optionally scoped to one country. */
  public getAllSoldToCodes(countryId?: number): Observable<any> {
    let params = new HttpParams();
    if (countryId) {
      params = params.set('countryId', String(countryId));
    }
    return this.http
      .get<any>(`${this.ACCOUNT_API}/allSoldToCodes`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /** A dedicated Sold-To-mapping-only bulk edit template, separate from the full Account import. */
  public downloadSoldToMappingTemplate(countryId?: number) {
    let params = new HttpParams();
    if (countryId) {
      params = params.set('countryId', String(countryId));
    }
    return this.http
      .get(`${this.ACCOUNT_API}/downloadSoldToMappingTemplate`, { responseType: 'blob', params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public importSoldToMapping(rows: { accountId: number; soldToCodes: string }[], countryId: number): Observable<any> {
    return this.http
      .post<any>(`${this.ACCOUNT_API}/importSoldToMapping`, { rows, countryId })
      .pipe(catchError(this.errorHandler.handleError));
  }

}
